import {
  EspAccount,
  SERVICE_PROVIDERS,
  EspAccountWithCustomMTAConfig,
} from 'modules/graphql/types';
import {
  SendingPlatform,
  ITERABLE_SMS,
  SMS_SERVICE_PROVIDERS,
  TWILIO,
  TELNYX,
  SMS_SENDER_TERMS_BY_SERVICE_PROVIDER,
  PHONE_NUMBER_LABEL_BY_SENDING_PLATFORM,
  ITERABLE,
  DYN,
  SENDGRID,
  MAILGUN,
  SPARKPOST,
  CUSTOM_EMAIL,
  WHATS_APP,
} from '../constants';

export const isTelnyxDirectAccount = (espAccount: EspAccount) =>
  espAccount.esp === SERVICE_PROVIDERS.TELNYX && !espAccount.isSenderIterable;

export const isTwilioDirectAccount = (espAccount: EspAccount) =>
  espAccount.esp === SERVICE_PROVIDERS.TWILIO && !espAccount.isSenderIterable;

export const isIterableSmsProviderAccount = (espAccount: EspAccount) =>
  SMS_SERVICE_PROVIDERS.includes(espAccount.esp) && !!espAccount.isSenderIterable;

const getSendingPlatformForCustomMTAEspAccount = (): SendingPlatform => {
  // TODO: Add logic to distinguish Custom Email and Custom Push
  return CUSTOM_EMAIL;
};

export const getSendingPlatformForEspAccount = (espAccount: EspAccount) => {
  const { esp } = espAccount;

  if (isIterableSmsProviderAccount(espAccount)) return ITERABLE_SMS;

  switch (esp) {
    case SERVICE_PROVIDERS.ITERABLE:
      return ITERABLE;
    case SERVICE_PROVIDERS.DYN:
      return DYN;
    case SERVICE_PROVIDERS.SENDGRID:
      return SENDGRID;
    case SERVICE_PROVIDERS.SPARKPOST:
    case SERVICE_PROVIDERS.SPARKPOST_EU:
    case SERVICE_PROVIDERS.SPARKPOST_ELITE:
      return SPARKPOST;
    case SERVICE_PROVIDERS.MAILGUN:
    case SERVICE_PROVIDERS.MAILGUN_EU:
      return MAILGUN;
    case SERVICE_PROVIDERS.TWILIO:
      return TWILIO;
    case SERVICE_PROVIDERS.TELNYX:
      return TELNYX;
    case SERVICE_PROVIDERS.INFOBIP:
      return WHATS_APP;
    case SERVICE_PROVIDERS.CUSTOM_MTA:
      // TODO: Add logic to distinguish Custom Email and Custom Push
      return getSendingPlatformForCustomMTAEspAccount();
    case SERVICE_PROVIDERS.COINBASE_MTA:
    case SERVICE_PROVIDERS.NO_OP:
    case SERVICE_PROVIDERS.SFMC:
      return undefined;
    default:
      return undefined;
  }
};

export const getPhoneNumberLabelForEspAccount = (espAccount: EspAccount) => {
  const sp = getSendingPlatformForEspAccount(espAccount);
  return PHONE_NUMBER_LABEL_BY_SENDING_PLATFORM[
    sp as typeof TWILIO | typeof TELNYX | typeof ITERABLE_SMS
  ];
};

export const getSmsSenderTermForEspAccount = (espAccount: EspAccount) =>
  SMS_SENDER_TERMS_BY_SERVICE_PROVIDER[
    espAccount.esp as typeof SERVICE_PROVIDERS.TWILIO | typeof SERVICE_PROVIDERS.TELNYX
  ];

export const mergeEspAccounts = (
  espAccountsGraphql: EspAccount[] = [],
  espAccountsRest: (EspAccount | EspAccountWithCustomMTAConfig)[] = [],
) => {
  const espAccountsRestById = espAccountsRest.reduce(
    (acc, espAccountRest) => {
      acc[espAccountRest.id] = espAccountRest;
      return acc;
    },
    {} as { [id: number]: EspAccount },
  );

  return espAccountsGraphql.map((espAccountGraphql) => {
    if (espAccountsRestById[espAccountGraphql.id]) {
      const { customMTAConfig, isSenderIterable, signingSecretPrefix } = espAccountsRestById[
        espAccountGraphql.id
      ] as EspAccountWithCustomMTAConfig;
      return { ...espAccountGraphql, customMTAConfig, isSenderIterable, signingSecretPrefix };
    } else {
      return espAccountGraphql;
    }
  });
};

export const accumulateEspAccountsBySendingPlatform = (
  acc: Record<SendingPlatform, EspAccount[]>,
  espAccount: EspAccount,
) => {
  const sendingPlatform = getSendingPlatformForEspAccount(espAccount) as SendingPlatform;
  if (!sendingPlatform) return acc;
  return !acc[sendingPlatform]
    ? { ...acc, [sendingPlatform]: [espAccount] }
    : { ...acc, [sendingPlatform]: [...acc[sendingPlatform], espAccount] };
};

export const accumulateEspAccountsBySendingPlatformWithIterableSms = (
  acc: Record<SendingPlatform, EspAccount[]>,
  espAccount: EspAccount,
) => {
  // Bucket IterableSMS accounts into their own SendingPlatform
  const sendingPlatform = isIterableSmsProviderAccount(espAccount)
    ? ITERABLE_SMS
    : (getSendingPlatformForEspAccount(espAccount) as SendingPlatform);
  if (!sendingPlatform) return acc;
  return !acc[sendingPlatform]
    ? { ...acc, [sendingPlatform]: [espAccount] }
    : { ...acc, [sendingPlatform]: [...acc[sendingPlatform], espAccount] };
};
